/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-10
 🧩 Description:
    API client for Extension Status module.
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

/**
 * Call forwarding status flags
 */
export interface CallForwardingStatus {
    bossSecretary: boolean;
    secretaryExtension: string | null;
    personalAssistant: boolean;
    followMe: boolean;
    dnd: boolean;
    cfi: boolean;
    cfb: boolean;
    cfn: boolean;
    cfu: boolean;
    callCompletion: boolean;
}

/**
 * Extension status entry for the list view
 */
export interface ExtensionStatusEntry {
    id: number;
    uuid: string;
    extension: string;
    name: string;
    enabled: boolean;
    forwarding: CallForwardingStatus;
}

/**
 * Device registration info
 */
export interface DeviceRegistration {
    username: string;
    channel: string;
    host: string;
    port: number;
    status: 'Registered' | 'Reachable' | 'Unknown' | 'Unregistered';
    userAgent: string;
    description: string;
}

/**
 * Full extension status details (for edit modal)
 */
export interface ExtensionStatusDetails {
    id: number;
    uuid: string;
    extension: string;
    name: string;
    personalAssistant: {
        enabled: boolean;
        press1: { module: string; destination: string } | null;
        press2: { module: string; destination: string } | null;
        press3: { module: string; destination: string } | null;
        press4: { module: string; destination: string } | null;
        default: { module: string; destination: string } | null;
    };
    bossSecretary: {
        enabled: boolean;
        timeGroupId: number | null;
    };
    followMe: {
        enabled: boolean;
        timeGroupId: number | null;
    };
    dnd: {
        enabled: boolean;
        timeGroupId: number | null;
    };
    callCompletion: {
        enabled: boolean;
        timeGroupId: number | null;
    };
    cfi: {
        enabled: boolean;
        module: string | null;
        destination: string | null;
        timeGroupId: number | null;
    };
    cfb: {
        enabled: boolean;
        module: string | null;
        destination: string | null;
        timeGroupId: number | null;
    };
    cfn: {
        enabled: boolean;
        module: string | null;
        destination: string | null;
        timeGroupId: number | null;
    };
    cfu: {
        enabled: boolean;
        module: string | null;
        destination: string | null;
        timeGroupId: number | null;
    };
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
    success: boolean;
    data: T;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

/**
 * Fetch extension status list
 */
export const fetchExtensionStatus = async (params: {
    page?: number;
    limit?: number;
    search?: string;
    domainId?: number;
}): Promise<ApiResponse<ExtensionStatusEntry[]>> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.domainId) queryParams.set('domainId', String(params.domainId));

    const response = await api.get<ApiResponse<ExtensionStatusEntry[]>>(
        `/api/telephony/extension-status?${queryParams.toString()}`
    );
    return response.data;
};

/**
 * Fetch device registrations for an extension
 */
export const fetchDeviceRegistrations = async (extensionId: number | string): Promise<DeviceRegistration[]> => {
    const response = await api.get<ApiResponse<DeviceRegistration[]>>(
        `/api/telephony/extension-status/${extensionId}/devices`
    );
    return response.data.data || [];
};

/**
 * Fetch full extension status details
 */
export const fetchExtensionStatusDetails = async (extensionId: number | string): Promise<ExtensionStatusDetails> => {
    const response = await api.get<ApiResponse<ExtensionStatusDetails>>(
        `/api/telephony/extension-status/${extensionId}`
    );
    return response.data.data;
};

/**
 * Toggle a forwarding feature
 */
export const toggleForwarding = async (
    extensionId: number | string,
    forwardType: string,
    enabled: boolean
): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(
        `/api/telephony/extension-status/${extensionId}/toggle`,
        { forwardType, enabled }
    );
    return response.data;
};

/**
 * Update forwarding settings
 */
export const updateForwardingSettings = async (
    extensionId: number | string,
    settings: Partial<ExtensionStatusDetails>
): Promise<{ success: boolean }> => {
    const response = await api.put<{ success: boolean }>(
        `/api/telephony/extension-status/${extensionId}`,
        settings
    );
    return response.data;
};
